/**
 * C-Bridge Publieke Legal Page
 *
 * Toont legal markdown documenten op basis van URL slug.
 * Toegankelijk via /legal/:slug (bv. /legal/privacy, /legal/terms)
 */

import { useParams, Link } from 'react-router-dom';

import privacyPolicy from '../../../../legal/privacy-policy.md?raw';
import termsAndConditions from '../../../../legal/terms-and-conditions.md?raw';
import cookiePolicy from '../../../../legal/cookie-policy.md?raw';
import sla from '../../../../legal/sla.md?raw';
import acceptableUsePolicy from '../../../../legal/acceptable-use-policy.md?raw';
import dpaTemplate from '../../../../legal/dpa-template.md?raw';

// ─── Slug → Document mapping ────────────────────────────────────────────────

const documents: Record<string, { title: string; content: string }> = {
  privacy: { title: 'Privacybeleid', content: privacyPolicy },
  terms: { title: 'Algemene Voorwaarden', content: termsAndConditions },
  cookies: { title: 'Cookiebeleid', content: cookiePolicy },
  sla: { title: 'Service Level Agreement', content: sla },
  'acceptable-use': { title: 'Acceptabel Gebruik', content: acceptableUsePolicy },
  dpa: { title: 'Data Processing Agreement', content: dpaTemplate },
};

// ─── Simple Markdown → HTML converter ────────────────────────────────────────

function markdownToHtml(md: string): string {
  let html = md
    // Escape HTML entities
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Code blocks (``` ... ```)
  html = html.replace(/```[\s\S]*?```/g, (match) => {
    const code = match.slice(3, -3).replace(/^\w*\n/, '');
    return `<pre class="bg-gray-100 rounded-lg p-4 overflow-x-auto text-sm my-4"><code>${code}</code></pre>`;
  });

  // Split into lines for block-level processing
  const lines = html.split('\n');
  const result: string[] = [];
  let inList = false;
  let inOrderedList = false;

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Headers
    if (line.startsWith('#### ')) {
      if (inList) { result.push('</ul>'); inList = false; }
      if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
      result.push(`<h4 class="text-lg font-semibold text-gray-900 mt-6 mb-2">${applyInline(line.slice(5))}</h4>`);
      continue;
    }
    if (line.startsWith('### ')) {
      if (inList) { result.push('</ul>'); inList = false; }
      if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
      result.push(`<h3 class="text-xl font-semibold text-gray-900 mt-8 mb-3">${applyInline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith('## ')) {
      if (inList) { result.push('</ul>'); inList = false; }
      if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
      result.push(`<h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4 pb-2 border-b border-gray-200">${applyInline(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith('# ')) {
      if (inList) { result.push('</ul>'); inList = false; }
      if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
      result.push(`<h1 class="text-3xl font-bold text-gray-900 mt-10 mb-6">${applyInline(line.slice(2))}</h1>`);
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      if (inList) { result.push('</ul>'); inList = false; }
      if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
      result.push('<hr class="my-8 border-gray-200" />');
      continue;
    }

    // Unordered list items
    if (/^\s*[-*]\s+/.test(line)) {
      if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }
      if (!inList) { result.push('<ul class="list-disc list-inside space-y-1 my-3 text-gray-700">'); inList = true; }
      const content = line.replace(/^\s*[-*]\s+/, '');
      result.push(`<li>${applyInline(content)}</li>`);
      continue;
    }

    // Ordered list items
    if (/^\s*\d+\.\s+/.test(line)) {
      if (inList) { result.push('</ul>'); inList = false; }
      if (!inOrderedList) { result.push('<ol class="list-decimal list-inside space-y-1 my-3 text-gray-700">'); inOrderedList = true; }
      const content = line.replace(/^\s*\d+\.\s+/, '');
      result.push(`<li>${applyInline(content)}</li>`);
      continue;
    }

    // Close lists if we hit a non-list line
    if (inList) { result.push('</ul>'); inList = false; }
    if (inOrderedList) { result.push('</ol>'); inOrderedList = false; }

    // Empty line
    if (line.trim() === '') {
      result.push('');
      continue;
    }

    // Regular paragraph
    result.push(`<p class="text-gray-700 leading-relaxed my-3">${applyInline(line)}</p>`);
  }

  if (inList) result.push('</ul>');
  if (inOrderedList) result.push('</ol>');

  return result.join('\n');
}

/** Apply inline markdown: bold, italic, links, inline code */
function applyInline(text: string): string {
  return text
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-brand hover:underline" target="_blank" rel="noopener noreferrer">$1</a>')
    // Email links
    .replace(/(\S+@\S+\.\S+)/g, '<a href="mailto:$1" class="text-brand hover:underline">$1</a>');
}

// ─── Component ───────────────────────────────────────────────────────────────

export function LegalPage() {
  const { slug } = useParams<{ slug: string }>();
  const doc = slug ? documents[slug] : undefined;

  if (!doc) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Document niet gevonden</h1>
          <p className="text-gray-600 mb-6">Het gevraagde document bestaat niet.</p>
          <Link
            to="/landing"
            className="inline-flex items-center gap-2 text-brand hover:underline font-medium"
          >
            &larr; Terug naar homepage
          </Link>
        </div>
      </div>
    );
  }

  const htmlContent = markdownToHtml(doc.content);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/landing" className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">CB</span>
            </div>
            <span className="text-xl font-bold text-gray-900">C-Bridge</span>
          </Link>
          <Link
            to="/landing"
            className="text-sm text-gray-500 hover:text-brand transition-colors"
          >
            &larr; Terug naar homepage
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div
          className="prose-custom"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} C-Bridge. Alle rechten voorbehouden.
            </p>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/legal/privacy" className="hover:text-brand transition-colors">Privacy</Link>
              <Link to="/legal/terms" className="hover:text-brand transition-colors">Voorwaarden</Link>
              <Link to="/legal/cookies" className="hover:text-brand transition-colors">Cookies</Link>
              <Link to="/legal/sla" className="hover:text-brand transition-colors">SLA</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
